import type { TFunction } from 'i18next';
import type { ParameterEntry } from '@config';

import { lendersGetLender, type ConfigurationResponse, type PmpComplianceCheckConfiguration } from '@pmp/api';

const PMP = 'PMP';
const BRS = 'BRS';

const list = (values: (string | undefined | null)[]): string => values.filter(Boolean).join(', ') || '-';

/**
 * The compliance configuration is the lender's, which lives in the broker service; everything else
 * comes from PMP's own configuration. Both are read only here, so a failing lender call leaves the
 * PMP parameters on screen rather than emptying the page.
 */
export const buildParameters = async (t: TFunction, configuration?: ConfigurationResponse): Promise<ParameterEntry[]> => {
  const text = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return t(value ? 'global.labels.yes' : 'global.labels.no');
    return String(value);
  };

  const entry = (source: string, key: string, value: unknown): ParameterEntry => ({
    key: `${source}:${key}`,
    source,
    name: t(`pmp.parameters.${key}`, { defaultValue: key }),
    value: text(value),
  });

  const pmp: ParameterEntry[] = configuration
    ? [
        entry(PMP, 'brands', list(configuration.brands.map(brand => brand.name ?? brand.code))),
        entry(PMP, 'defaultTechnicalProductCode', configuration.defaultTechnicalProductCode),
        entry(PMP, 'rateAdaptationBands', list(configuration.rateAdaptationBands.map(band => band.name))),
        entry(PMP, 'notesEditWindow', configuration.notes?.defaultEditWindowDays),
        entry(PMP, 'version', configuration.pmpVersion),
        entry(BRS, 'version', configuration.brsVersion),
      ]
    : [];

  let compliance: PmpComplianceCheckConfiguration | undefined;
  try {
    const { data } = await lendersGetLender();
    compliance = data?.complianceCheckConfiguration;
  } catch {
    compliance = undefined;
  }

  const brs: ParameterEntry[] = compliance
    ? [
        entry(BRS, 'defaultFirmStatus', compliance.defaultFirmStatus),
        entry(BRS, 'defaultIntermediaryStatus', compliance.defaultIntermediaryStatus),
        entry(BRS, 'firmToReviewPermissionLevel', compliance.firmToReviewPermissionLevel),
        entry(BRS, 'intermediaryToReviewPermissionLevel', compliance.intermediaryToReviewPermissionLevel),
        entry(
          BRS,
          'firmPermissionLevelPerStatus',
          list((compliance.firmPermissionLevelPerStatus ?? []).map(item => `${item.complianceStatus}: ${item.permissionLevel}`)),
        ),
        entry(
          BRS,
          'intermediaryPermissionLevelPerStatus',
          list((compliance.intermediaryPermissionLevelPerStatus ?? []).map(item => `${item.complianceStatus}: ${item.permissionLevel}`)),
        ),
        entry(BRS, 'resetReviewRequiredFlagAfterMonths', compliance.resetReviewRequiredFlagAfterMonths),
        entry(BRS, 'reviewOnBmpChangesEnabled', compliance.reviewOnBmpChangesEnabled),
        entry(BRS, 'periodicCheckEnabled', compliance.periodicCheckEnabled),
      ]
    : [];

  return [...pmp, ...brs];
};
